export type ColumnType =
  | "currency"
  | "percentage"
  | "ratio"
  | "date"
  | "number"
  | "string"

const CURRENCY_RE =
  /(?:^|_)(spend|revenue|cost|price|cpa|cpc|cpm|aov|ltv|budget|amount|gmv|sales)(?:$|_| )/i
const PERCENTAGE_RE = /(?:^|_)(rate|ctr|cvr|percentage|pct)(?:$|_| )/i
const RATIO_RE = /(?:^|_)(roas|roi|ratio|multiplier|factor)(?:$|_| )/i

export function detectColumnType(
  columnKey: string,
  sampleValue: string | number,
): ColumnType {
  const name = (columnKey.split(".").pop() ?? columnKey).toLowerCase()

  if (RATIO_RE.test(name)) return "ratio"
  if (CURRENCY_RE.test(name)) return "currency"
  if (PERCENTAGE_RE.test(name)) return "percentage"

  // Detect ISO date strings in the value itself
  if (
    typeof sampleValue === "string" &&
    /^\d{4}-\d{2}(-\d{2})?/.test(sampleValue)
  ) {
    return "date"
  }

  if (typeof sampleValue === "number") return "number"
  return "string"
}

/** Abbreviate absolute value to K/M. Returns null if < 1000. */
function abbreviate(absVal: number): string | null {
  if (absVal >= 1_000_000) return `${(absVal / 1_000_000).toFixed(1)}M`
  if (absVal >= 1_000) {
    const formatted = (absVal / 1_000).toFixed(1)
    if (formatted === "1000.0") return `${(absVal / 1_000_000).toFixed(1)}M`
    return `${formatted}K`
  }
  return null
}

export function formatDisplayValue(
  value: string | number,
  type: ColumnType,
): string {
  switch (type) {
    case "ratio": {
      const num = typeof value === "number" ? value : parseFloat(String(value))
      if (isNaN(num)) return String(value)
      return `${num.toFixed(2)}x`
    }
    case "currency": {
      const num = typeof value === "number" ? value : parseFloat(String(value))
      if (isNaN(num)) return String(value)
      const sign = num < 0 ? "-" : ""
      const abs = Math.abs(num)
      const abbr = abbreviate(abs)
      if (abbr) return `${sign}$${abbr}`
      return `${sign}$${abs.toFixed(2)}`
    }
    case "percentage": {
      const num = typeof value === "number" ? value : parseFloat(String(value))
      if (isNaN(num)) return String(value)
      return `${num.toFixed(2)}%`
    }
    case "date": {
      const s = String(value)
      const full = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (full) return `${full[2]}/${full[3]}/${full[1]}`
      const monthOnly = s.match(/^(\d{4})-(\d{2})$/)
      if (monthOnly) return `${monthOnly[2]}/${monthOnly[1]}`
      return s
    }
    case "number": {
      if (typeof value !== "number") return String(value)
      const abbr = abbreviate(Math.abs(value))
      if (abbr) return value < 0 ? `-${abbr}` : abbr
      if (!Number.isInteger(value)) return value.toFixed(2)
      return value.toLocaleString()
    }
    default:
      return String(value)
  }
}

/** Simple number abbreviation (for chart Y-axis ticks where column type is unknown) */
export function formatCellValue(value: string | number): string {
  return formatDisplayValue(
    value,
    typeof value === "number" ? "number" : "string",
  )
}

export function cleanColumnName(key: string): string {
  // "company_media_metrics_ads.spend" → "spend"
  const parts = key.split(".")
  const name = parts[parts.length - 1]
  return name.replace(/_/g, " ")
}
